/**
 * Layout de tabela alinhado ao PhotoBlitiz /album (listagem de álbuns).
 * Cabeçalho + sobreposição do card alinhados ao /product (borracharia_major).
 */
import styled from 'styled-components';
import TableContainer from '@mui/material/TableContainer';
import CustomButton from '../../components/Form/Button';
import { PageHeader as TradingPageHeader } from '../Trading/styles';

/** Título acima do gradiente; mais espaço até o card (card mais baixo na página) */
export const StrategyPageHeader = styled(TradingPageHeader)`
    z-index: 2;
    margin-bottom: 28px;

    @media (min-width: 768px) {
        margin-bottom: 36px;
    }
`;

const OrangeAccentCard = styled.div`
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(242, 146, 5, 0.2);
    border: 1px solid rgba(242, 146, 5, 0.2);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    position: relative;

    @media (min-width: 768px) {
        border-radius: 20px;
        padding: 28px;
    }

    &:hover {
        box-shadow: 0 15px 50px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(242, 146, 5, 0.3);
    }
`;

export const StrategyProductCard = styled(OrangeAccentCard)`
    position: relative;
    z-index: 2;
    margin-bottom: 32px;
`;

/** Mesmo padrão PhotoBlitiz Album: tabela “solta” dentro do card, sem caixa extra */
export const StrategyTableContainer = styled(TableContainer)`
    background: transparent !important;
    border: none !important;
    border-radius: 0 !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
    box-shadow: none !important;

    .MuiTable-root {
        background: transparent !important;
        min-width: 600px !important;
    }

    .MuiTableHead-root {
        background: rgba(242, 146, 5, 0.1) !important;

        .MuiTableCell-head {
            color: #f29205 !important;
            font-weight: 700 !important;
            border-bottom: 2px solid rgba(242, 146, 5, 0.3) !important;
            padding: 12px 8px !important;
            white-space: nowrap !important;

            @media (min-width: 768px) {
                padding: 16px !important;
            }
        }
    }

    .MuiTableBody-root .MuiTableRow-root {
        transition: all 0.2s ease !important;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;

        &:hover {
            background: rgba(242, 146, 5, 0.05) !important;
        }

        .MuiTableCell-body {
            color: #e0e0e0 !important;
            padding: 12px 8px !important;
            border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;

            @media (min-width: 768px) {
                padding: 16px !important;
            }
        }
    }

    .MuiTableFooter-root {
        background: rgba(242, 146, 5, 0.05) !important;

        .MuiTableCell-footer {
            border-top: 2px solid rgba(242, 146, 5, 0.3) !important;
            padding: 12px 8px !important;

            @media (min-width: 768px) {
                padding: 16px !important;
            }
        }
    }
`;

/**
 * Card centralizado e mais estreito.
 * margin-top positivo: desce o card, deixa mais gradiente visível acima.
 */
export const StrategyCardColumn = styled.div`
    width: 100%;
    max-width: 520px;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    z-index: 2;
    margin-top: 16px;

    @media (min-width: 576px) {
        max-width: 580px;
        margin-top: 20px;
    }

    @media (min-width: 992px) {
        max-width: 640px;
        margin-top: 24px;
    }

    ${StrategyTableContainer} .MuiTable-root {
        min-width: 0 !important;
        width: 100%;
        table-layout: fixed;
    }

    ${StrategyTableContainer} .MuiTableHead-root .MuiTableCell-head:first-of-type,
    ${StrategyTableContainer} .MuiTableBody-root .MuiTableCell-body:first-of-type {
        width: auto;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    ${StrategyTableContainer} .MuiTableHead-root .MuiTableCell-head:last-of-type,
    ${StrategyTableContainer} .MuiTableBody-root .MuiTableCell-body:last-of-type {
        width: 112px;
        max-width: 112px;
    }
`;

/**
 * Mesma ideia do StrategyCardColumn (card centralizado), porém largura para listagens admin
 * com várias colunas — sem forçar largura da última coluna (112px).
 */
export const StrategyWideCardColumn = styled.div`
    width: 100%;
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    z-index: 2;
    margin-top: 16px;

    @media (min-width: 576px) {
        margin-top: 20px;
    }

    @media (min-width: 992px) {
        max-width: 1100px;
        margin-top: 24px;
    }

    ${StrategyTableContainer} .MuiTable-root {
        min-width: 640px !important;
        width: 100%;
        table-layout: auto;
    }
`;

export const StrategyActionButtons = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    .MuiIconButton-root {
        color: #00d4ff !important;
        transition: all 0.2s ease !important;

        &:hover {
            background: rgba(0, 212, 255, 0.1) !important;
            transform: scale(1.1);
        }

        &[aria-label='delete'] {
            color: #ff6b6b !important;

            &:hover {
                background: rgba(255, 107, 107, 0.1) !important;
            }
        }
    }
`;

export const StrategyPaginationInfo = styled.span`
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    padding: 12px 0;
`;

export const StrategyPaginationButton = styled.button`
    background: rgba(242, 146, 5, 0.2);
    border: 1px solid rgba(242, 146, 5, 0.5);
    color: #f29205;
    min-width: 100px;
    height: 40px;
    padding: 0 14px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-weight: 600;
    font-size: 14px;
    white-space: nowrap;

    &:hover:not(:disabled) {
        background: rgba(242, 146, 5, 0.3);
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }

    svg {
        font-size: 18px;
        flex-shrink: 0;
    }
`;

export const StrategyLoadingButton = styled(CustomButton)`
    width: 100%;
    margin-top: 32px;
`;

export const StrategyEmptyState = styled.div`
    text-align: center;
    padding: 80px 20px;
    color: rgba(255, 255, 255, 0.6);

    i {
        font-size: 64px;
        color: rgba(255, 255, 255, 0.3);
        margin-bottom: 24px;
        display: block;
    }

    p {
        font-size: 24px;
        font-weight: 600;
        color: #ffffff;
        margin: 0 0 8px 0;
    }

    span {
        font-size: 16px;
        color: rgba(255, 255, 255, 0.5);
    }
`;

export const StrategyTableWrapper = styled.div`
    padding: 16px;
    padding-top: 0;
    margin-top: 20px;

    @media (min-width: 768px) {
        margin-top: 24px;
        padding: 32px;
        padding-top: 0;
    }
`;

export const StrategyNameCell = styled.div`
    color: #e0e0e0;
    font-size: 14px;
    font-weight: 500;
`;

/** Barra de pesquisa (listagem de pares de moedas, etc.) */
export const StrategySearchWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
    width: 100%;

    svg {
        color: #f29205;
        font-size: 22px;
        flex-shrink: 0;
    }

    input {
        flex: 1;
        min-width: 0;
        background: rgba(255, 255, 255, 0.06);
        border: 1px solid rgba(242, 146, 5, 0.25);
        border-radius: 10px;
        padding: 10px 14px;
        color: #e0e0e0;
        font-size: 14px;
        outline: none;
        transition: border-color 0.2s;

        &::placeholder {
            color: rgba(255, 255, 255, 0.35);
        }

        &:focus {
            border-color: rgba(242, 146, 5, 0.55);
            box-shadow: 0 0 0 2px rgba(242, 146, 5, 0.12);
        }
    }
`;

/** Formulário nova/editar estratégia — alinhado a Meta/Save e Bank */
export const StrategyFormNarrow = styled.div`
    max-width: 520px;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
    position: relative;
    z-index: 2;
    margin-top: 16px;

    @media (min-width: 576px) {
        margin-top: 20px;
    }

    @media (min-width: 992px) {
        margin-top: 24px;
    }
`;
